import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Filter, SlidersHorizontal, ShoppingCart, Eye } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useCatalog } from '../hooks/useCatalog';
import '../styles/catalog.css';

const sortOptions = [
  { value: 'recent', label: 'Mas recientes' },
  { value: 'price_asc', label: 'Precio ascendente' },
  { value: 'price_desc', label: 'Precio descendente' }
];

const CatalogPage = ({ forcedCategory = 'libro', title = 'Catalogo' }) => {
  const [search, setSearch] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [sort, setSort] = useState('recent');
  const navigate = useNavigate();
  const location = useLocation();
  const { addItem, formatPrice } = useCart();

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q') || '';
    setSearch(query);
  }, [location.search]);

  const filters = useMemo(() => ({
    category: forcedCategory,
    q: search || undefined,
    minPrice: minPrice || undefined,
    maxPrice: maxPrice || undefined,
    sort
  }), [forcedCategory, maxPrice, minPrice, search, sort]);

  const { books, loading } = useCatalog(filters);

  return (
    <div className="catalog-page">
      <section className="catalog-page__hero">
        <h1>{title}</h1>
        <p>Explora y filtra el catalogo con una experiencia de compra moderna.</p>
      </section>

      <section className="catalog-page__filters">
        <label>
          <Filter size={16} />
          <input
            type="search"
            placeholder="Buscar por titulo o autor"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <label>
          <SlidersHorizontal size={16} />
          <input
            type="number"
            min="0"
            placeholder="Precio minimo"
            value={minPrice}
            onChange={(event) => setMinPrice(event.target.value)}
          />
        </label>
        <label>
          <SlidersHorizontal size={16} />
          <input
            type="number"
            min="0"
            placeholder="Precio maximo"
            value={maxPrice}
            onChange={(event) => setMaxPrice(event.target.value)}
          />
        </label>
        <label>
          <select value={sort} onChange={(event) => setSort(event.target.value)}>
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
      </section>

      <section className="catalog-page__grid">
        {loading && Array.from({ length: 8 }).map((_, index) => (
          <div key={`sk-${index}`} className="catalog-card skeleton" />
        ))}
        {!loading && books.map((book) => (
          <motion.article
            key={book.id}
            className="catalog-card"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="catalog-card__cover">
              <img src={book.coverUrl} alt={book.title} loading="lazy" />
            </div>
            <div className="catalog-card__body">
              <h3>{book.title}</h3>
              <p>{book.author}</p>
              <span className="catalog-card__category">{book.category}</span>
              <strong>{formatPrice(book.price)}</strong>
            </div>
            <div className="catalog-card__actions">
              <button type="button" onClick={() => navigate(`/libro/${book.id}`)}>
                <Eye size={16} />
                Ver detalles
              </button>
              <button type="button" className="primary" onClick={() => addItem(book)}>
                <ShoppingCart size={16} />
                Agregar al carrito
              </button>
            </div>
          </motion.article>
        ))}
      </section>
    </div>
  );
};

export default CatalogPage;
